<div class="camera-scanner-container min-h-screen mobile-only-scanner">

    <!-- Redirect Message for Non-Mobile Screens -->
    <div *ngIf="isRedirecting()" class="fixed inset-0 bg-gray-900 flex items-center justify-center z-50">
        <div class="bg-gray-800 rounded-2xl p-8 text-center max-w-md mx-4">
            <div class="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
                    </path>
                </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-2">Redirecting...</h3>
            <p class="text-gray-300">Screen size changed to tablet/desktop. Redirecting to mobile-only page...</p>
        </div>
    </div>

    <!-- Camera Permission Popup - Using PrimeNG components -->
    <p-dialog [visible]="showCameraPermissionPopup()" [modal]="true" [closable]="false" [draggable]="false"
        [resizable]="false" [style]="{ width: '90vw', maxWidth: '400px' }"
        [contentStyle]="{ padding: '2rem', textAlign: 'center' }" [header]="''">

        <!-- Camera Icon using PrimeNG Avatar -->
        <p-avatar icon="pi pi-camera" size="xlarge" styleClass="permission-modal-icon mb-6" shape="circle">
        </p-avatar>

        <!-- Title - Dynamic based on error type -->
        <h2 class="permission-modal-title text-xl font-bold mb-4">
            @if (cameraErrorType() === 'in-use') {
            Camera Already in Use
            } @else if (cameraErrorType() === 'not-found') {
            No Camera Found
            } @else {
            Camera Permission Required
            }
        </h2>

        <!-- Description - Dynamic based on error type -->
        <p class="permission-modal-description mb-6 text-sm leading-relaxed">
            @if (cameraErrorType() === 'in-use') {
            Your camera is currently being used by another tab or application.
            } @else if (cameraErrorType() === 'not-found') {
            No camera device was detected on your device.
            } @else {
            Your camera is required for scanning QR codes, capturing images, or using video features.
            }
        </p>

        <!-- Instructions - Dynamic based on error type -->
        @if (cameraErrorType() === 'in-use') {
        <!-- Camera in use instructions -->
        <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left">
            <p class="text-xs text-orange-800 leading-relaxed mb-3">
                <strong>To fix this:</strong>
            </p>
            <ol class="text-xs text-orange-800 leading-relaxed list-decimal list-inside space-y-2">
                <li>Close other tabs or apps using the camera</li>
                <li>Make sure no video calls are active</li>
                <li>Click the <strong>"Refresh"</strong> button below</li>
            </ol>
        </div>
        } @else if (cameraErrorType() === 'not-found') {
        <!-- No camera found instructions -->
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
            <p class="text-xs text-red-800 leading-relaxed mb-3">
                <strong>Possible solutions:</strong>
            </p>
            <ul class="text-xs text-red-800 leading-relaxed list-disc list-inside space-y-2">
                <li>Check if your device has a camera</li>
                <li>Try using a different device</li>
                <li>Ensure camera drivers are installed</li>
            </ul>
        </div>
        } @else {
        <!-- Default permission instructions -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <p class="text-xs text-blue-800 leading-relaxed mb-3">
                <strong>To enable camera access:</strong>
            </p>
            <ol class="text-xs text-blue-800 leading-relaxed list-decimal list-inside space-y-2">
                <li>Click the camera icon <strong>🔒</strong> in your browser's address bar</li>
                <li>Select <strong>"Allow"</strong> for camera permissions</li>
                <li>Click the <strong>"Refresh"</strong> button below</li>
            </ol>
        </div>
        }

        <!-- Refresh Button -->
        <div class="permission-buttons-container flex justify-center">
            <p-button label="Refresh" icon="pi pi-refresh" severity="primary" size="large"
                styleClass="permission-allow-button w-full" (onClick)="refreshPage()">
            </p-button>
        </div>
    </p-dialog>
    <!-- Full-Page Loader for Upload/Validation -->
    <div *ngIf="isUploading()" class="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-50">
        <div class="text-center">
            <!-- Spinner -->
            <div class="w-20 h-20 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-6">
            </div>
            <!-- Validation Message -->
            <h3 class="text-2xl font-bold text-white mb-2">Validating QR</h3>
            <p class="text-gray-300">Please wait...</p>
        </div>
    </div>

    <!-- Main Scanner Interface - Dark theme matching screenshot -->
    <div class="min-h-screen flex flex-col">
        <!-- Status Bar (simulated) -->

        <!-- SCANNING Header -->
        <div class="text-center py-10">
            <h3 class="text-1xl font-bold text-white uppercase tracking-wider">Scanning</h3>
        </div>

        <!-- Main Content Area -->
        <div class="flex-1 flex flex-col items-center m-10 px-4">
            <!-- Camera View Container -->
            <div class="relative mb-20">
                <!-- Torch/Flashlight Toggle Button (disabled) -->

                <div *ngIf="isCameraActive() && isTorchSupported()" class="absolute top-2 right-2 z-50">
                    <button (click)="toggleTorch()"
                        [class]="isTorchOn() ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-700 hover:bg-gray-600'"
                        class="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-200 transform hover:scale-105 border-2 border-white">
                        <i *ngIf="!isTorchOn()" class="pi pi-bolt text-xl"></i>
                        <i *ngIf="isTorchOn()" class="pi pi-sun text-xl"></i>
                    </button>
                </div>


                <!-- Idle Warning Banner -->
                @if (showIdleWarning()) {
                <div class="absolute top-16 left-0 right-0 z-40 mx-4">
                    <div class="bg-orange-500 text-white rounded-lg p-4 shadow-lg animate-pulse">
                        <div class="flex items-center justify-between">
                            <div class="flex-1">
                                <p class="font-bold text-sm mb-1">Camera will close soon</p>
                                <p class="text-xs">Closing in {{ idleTimeRemaining() }} seconds due to inactivity</p>
                            </div>
                            <button (click)="updateActivity()"
                                class="bg-white text-orange-500 px-4 py-2 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors ml-3">
                                Keep Active
                            </button>
                        </div>
                    </div>
                </div>
                }

                <!-- Video Container - Always present for ViewChild access -->
                <div class="relative">
                    <!-- Video Element - Always present but hidden when not active -->
                    <video #videoElement [style.display]="isCameraActive() ? 'block' : 'none'"
                        [ngClass]="{'zoomed': isCameraActive()}"
                        [style.width]="isCameraActive() ? laavaZoom() * 100 + 'vw' : '320px'"
                        [style.height]="isCameraActive() ? laavaZoom() * 100 + 'vw' : '320px'"
                        [style.max-width]="isCameraActive() ? laavaZoom() * 100 + 'vw' : '320px'"
                        [style.max-height]="isCameraActive() ? laavaZoom() * 100 + 'vw' : '320px'"
                        class="rounded-2xl object-cover shadow-[0_0_20px_rgba(255,255,255,0.3)]" autoplay playsinline
                        muted>
                    </video>

                    <!-- Camera Active State Overlays -->
                    <div *ngIf="isCameraActive()" class="absolute inset-0 pointer-events-none">
                        <!-- SVG Camera Frame - Outside the video container -->
                        <div class="absolute -top-2 -left-2 -right-2 -bottom-2 pointer-events-none z-40">
                            <img src="assets/logos/camera-frame.svg" alt="Camera Frame"
                                class="w-full h-full object-contain">
                        </div>

                        <!-- QR Detection Canvas Overlay -->
                        <canvas #qrCanvas class="absolute top-0 left-0 w-80 h-80 rounded-2xl pointer-events-none z-30"
                            width="640" height="480">
                        </canvas>

                        <!-- Scanning Overlay -->
                        <div class="absolute top-0 left-0 w-80 h-80 pointer-events-none z-20">
                            <!-- Center Logo Overlay -->
                            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div class="w-24 h-24 flex items-center justify-center">
                                    <!-- G Logo placeholder for visual balance -->
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Processing Overlay (during image capture only) -->
                    @if (isCapturing() && !isUploading()) {
                    <div
                        class="absolute inset-0 bg-black bg-opacity-60 rounded-2xl flex items-center justify-center z-50">
                        <div class="text-center p-6 w-full max-w-[80%]">
                            <!-- Progress Text -->
                            <p class="text-white font-semibold text-lg mb-4">
                                Capturing image {{ captureProgress() }} of {{ totalCaptureCount() }}
                            </p>

                            <!-- Progress Bar -->
                            <div class="w-full bg-gray-700 rounded-full h-2.5 mb-2">
                                <div class="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                    [style.width.%]="(captureProgress() / totalCaptureCount()) * 100">
                                </div>
                            </div>

                            <!-- Small subtext -->
                            <p class="text-gray-300 text-xs">Hold still...</p>
                        </div>
                    </div>
                    }
                </div>

                <!-- Captured Image Display -->
                <div *ngIf="capturedImage() && !isCameraActive()" class="relative">
                    <div
                        class="w-80 h-80 rounded-2xl overflow-hidden border-2 border-white shadow-[0_0_20px_rgba(255,255,255,0.3)]">
                        <img [src]="capturedImage()!" alt="Captured QR code" class="w-full h-full object-cover">
                    </div>
                </div>

                <!-- Camera Not Active State -->
                <div *ngIf="!isCameraActive() && !capturedImage()"
                    class="w-80 h-80 rounded-2xl border-2 border-dashed border-gray-600 flex items-center justify-center">
                    <div class="text-center">
                        <i class="pi pi-camera text-6xl text-gray-600 mb-4"></i>
                        <p class="text-gray-400">Camera not active</p>
                    </div>
                </div>
            </div>

            <!-- Status Indicator - Bottom section like screenshot -->
            <div class="flex items-center justify-center gap-5">
                <!-- Scanner Icon - Custom Box Design -->
                <div class="w-12 h-12 flex items-center justify-center">
                    <img src="assets/logos/scanning-icon.svg" alt="Scanning" class="w-full h-full object-contain" />
                </div>

                <!-- Status Text -->
                <div class="text-white font-semibold">
                    <span
                        *ngIf="isCameraActive() && !isCapturing() && !isUploading() && !isRetrying() && !isTagNotFound()">SCANNING...</span>
                    <span *ngIf="isCapturing() && !isUploading()">PROCESSING...</span>
                    <span *ngIf="isUploading()">VALIDATING...</span>
                </div>
            </div>
            <!-- Camera Loading State -->
            <div *ngIf="!isCameraActive() && !capturedImage() && permissions().camera === 'granted'"
                class="mt-8 flex items-center justify-center gap-3">
                <span class="text-gray-300">Starting camera...</span>
            </div>
        </div>
    </div>

    <!-- Hidden Canvas for Image Processing -->
    <canvas #canvasElement style="display: none;" width="640" height="480"></canvas>

    <!-- Powered By reGenesis Logo - Fixed position in bottom center -->
    <div class="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
        <div class="flex items-center gap-2 bg-opacity-50 rounded-lg px-3 py-2">
            <!-- G Logo -->
            <div class="w-6 h-6 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 103 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M55.2812 49.0938L55.2734 49.0244C55.2411 48.7321 55.2749 48.4358 55.373 48.1582C55.4711 47.8808 55.6314 47.6281 55.8418 47.4189C56.0522 47.2098 56.308 47.0497 56.5898 46.9512C56.8719 46.8527 57.1733 46.8181 57.4707 46.8506L57.54 46.8584H98.9297L98.999 46.8506C99.2965 46.8181 99.5978 46.8527 99.8799 46.9512C100.162 47.0497 100.418 47.2099 100.628 47.4189C100.838 47.628 100.999 47.8806 101.097 48.1582C101.195 48.436 101.229 48.7323 101.196 49.0244L101.188 49.0938V67.2783C101.188 75.9758 99.4593 83.1888 95.877 88.6504C92.3348 94.0506 86.9061 97.8523 79.2422 99.6631H24.1992C16.5278 97.8795 11.0953 94.0781 7.55078 88.6709C3.96612 83.2024 2.23634 75.976 2.23633 67.2783V35.8955C2.23633 25.3331 4.75403 16.9519 10.0137 11.2168C15.2548 5.50188 23.4115 2.20609 35.1465 2.20605H68.2969C79.3371 2.20605 87.1979 4.95069 92.4385 9.85352C97.6777 14.7552 100.491 21.9945 101.06 31.3877L101.064 31.4717L101.081 31.5537C101.137 31.8418 101.124 32.1394 101.043 32.4219C100.962 32.7042 100.815 32.9641 100.613 33.1807C100.412 33.3973 100.161 33.5646 99.8809 33.668C99.6009 33.7713 99.3 33.8079 99.0029 33.7744L98.9316 33.7666H85.8848C84.9712 33.7665 84.4772 33.5176 84.1875 33.2207C83.8797 32.9051 83.6181 32.3455 83.5547 31.377L83.5527 31.3604L83.5518 31.3428L83.458 30.459C82.9355 26.1284 81.6088 23.1073 79.084 21.2168C76.4562 19.2493 72.8007 18.7275 68.2969 18.7275H35.1465C30.1382 18.7276 26.1348 19.389 23.4551 22.0088C20.7744 24.6296 19.7373 28.8922 19.7373 35.1875V67.8936C19.7373 74.1361 20.8528 78.3565 23.5713 80.9521C26.2805 83.5389 30.2685 84.2041 35.1465 84.2041H68.3535C73.223 84.2041 77.2106 83.5383 79.9219 80.9521C82.6424 78.3568 83.7636 74.137 83.7637 67.8936V61.7383H57.4619L57.4092 61.7432C57.2432 61.7571 57.0761 61.7571 56.9102 61.7432L56.4795 61.707L56.1152 61.9404C55.708 62.2022 55.3782 62.5688 55.1602 63.002C54.9693 63.3814 54.8709 63.7997 54.8721 64.2227L54.875 64.3154V77.8447C54.8701 78.3484 54.6666 78.8317 54.3057 79.1904C53.9428 79.5511 53.45 79.7567 52.9336 79.7598H39.8232C39.3067 79.7568 38.8131 79.5511 38.4502 79.1904C38.0877 78.83 37.8838 78.3433 37.8809 77.8369V64.3223C37.8839 63.8161 38.0878 63.33 38.4502 62.9697C38.7678 62.654 39.1853 62.4573 39.6309 62.4111L39.8232 62.4004H52.9688L52.9971 62.3994C53.4303 62.3802 53.8522 62.2515 54.2227 62.0254C54.5932 61.7993 54.9005 61.4825 55.1152 61.1045L55.3115 60.7588L55.2764 60.3633C55.2538 60.1099 55.2538 59.8549 55.2764 59.6016L55.2812 59.5449V49.0938Z"
                        fill="white" stroke="white" stroke-width="2.54389" />
                </svg>
            </div>
            <!-- Powered By reGenesis Text -->
            <div class="text-white text-xs font-medium">
                Powered By reGenesis
            </div>
        </div>
    </div>

</div>